/**
 * Platební a dodací podmínky (#info-platba) a Reklamační řád (#info-reklamace).
 *
 * Samostatné stránky vyžaduje Comgate před schválením plateb kartou
 * (ticket #695723, 20. 10. 2026): „reklamační podmínky" a „dodací a platební
 * podmínky" musí být na webu dohledatelné zvlášť, s identifikací provozovatele.
 * Obsah vychází z Obchodních podmínek (#podminky) a nesmí jim odporovat.
 * Identifikace firem ověřena v ARES 24. 10. 2026.
 */

const OPERATOR = (
  <p className="text-[14px] text-ink-800 leading-relaxed">
    Provozovatel a prodávající: <b>STALAGMIT, a.s.</b>, IČ 27377342, DIČ CZ27377342,
    se sídlem Varšavská 715/36, 120 00 Praha 2, zapsaná v obchodním rejstříku.
    Provozovna: Sportovní areál Sobáčov, Sobáčov 111, 783 21 Mladeč.
    Kontakt: <a href="tel:+420775140705" className="font-semibold text-ink-900 hover:text-brand-600">+420 775 140 705</a>,{' '}
    <a href="mailto:rezervace.sobacov@gmail.com" className="font-semibold text-ink-900 hover:text-brand-600">rezervace.sobacov@gmail.com</a>.
  </p>
)

function Shell({ title, lead, children }: { title: string; lead: string; children: React.ReactNode }) {
  return (
    <section className="bg-paper py-12 md:py-20 min-h-screen">
      <div className="container-x max-w-3xl">
        <div className="text-[10px] uppercase tracking-[0.30em] text-brand-500 font-bold flex items-center gap-2">
          <span className="inline-block w-8 h-px bg-brand-500" />
          Sobáčov · Fishing &amp; Sport Resort
        </div>
        <h1 className="h-display text-4xl md:text-5xl mt-3">{title}</h1>
        <p className="text-ink-700 mt-3 text-sm">{lead}</p>
        <div className="mt-8 card p-6 md:p-7">{OPERATOR}</div>
        <div className="mt-8 space-y-8">{children}</div>
        <a href="#top" onClick={() => { window.location.hash = '' }} className="btn-outline-dark mt-8">
          Zpět na hlavní stránku
        </a>
      </div>
    </section>
  )
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-6 md:p-7">
      <h2 className="font-bold text-lg text-ink-900 tracking-tight">{title}</h2>
      <div className="mt-3 text-[15px] text-ink-800 leading-relaxed space-y-2">{children}</div>
    </div>
  )
}

export function PlatbaDodani() {
  return (
    <Shell
      title="Platební a dodací podmínky"
      lead="Jak se u nás platí a co po zaplacení dostanete. Podrobnosti ke stornu a reklamaci najdete v Obchodních podmínkách a Reklamačním řádu."
    >
      <Block title="1. Co nabízíme a jak objednáte">
        <p>
          Na webu rezervujete ubytování (pokoje penzionu, rybářské chatky, tiny house, bivak,
          karavanová stání), sportoviště (tenisové kurty, plážový volejbal) a
          dárkové poukazy. Rezervace se dokončuje přímo zde na webu: vyberete termín, konkrétní
          jednotku a zadáte jméno a telefon. Cena se zobrazuje průběžně během výběru a je
          konečná před odesláním rezervace.
        </p>
        <p>
          Všechny ceny jsou uvedeny v korunách českých (CZK) včetně DPH; k ceně nepřipočítáváme
          žádné další poplatky. Ceník najdete v sekci{' '}
          <a href="#info-cenik" className="font-semibold text-ink-900 hover:text-brand-600">Celkový ceník</a>.
        </p>
      </Block>

      <Block title="2. Způsoby platby">
        <ul className="list-disc list-inside space-y-1.5">
          <li>
            <b>Platební kartou online</b> (Visa, Mastercard; zabezpečeno 3-D Secure). Po odeslání rezervace
            vás přesměrujeme na zabezpečenou stránku platební brány Comgate, kde zadáte údaje karty a platbu
            potvrdíte ve své bance (např. v mobilní aplikaci nebo SMS kódem). Poté se vrátíte na náš web
            a rezervaci vám potvrdíme SMS.{' '}
            <a href="https://help.comgate.eu/v1/docs/cs/platby-kartou" target="_blank" rel="noopener" className="font-semibold text-ink-900 hover:text-brand-600 underline">Jak probíhá platba kartou</a>
          </li>
          <li>
            <b>Platebním tlačítkem banky</b> (online bankovní převod). Na stránce platební brány vyberete svou
            banku, přihlásíte se do internetového bankovnictví, kde už je platba předvyplněná, a jen ji
            potvrdíte. Po připsání platby rezervaci potvrdíme.{' '}
            <a href="https://help.comgate.eu/docs/bankovni-prevody" target="_blank" rel="noopener" className="font-semibold text-ink-900 hover:text-brand-600 underline">Jak probíhá platba převodem</a>
          </li>
          <li><b>Bankovním převodem</b> na účet provozovatele — variabilní symbol obdržíte v potvrzení rezervace.</li>
          <li><b>Na místě</b> při příjezdu (hotově nebo kartou), pokud nebyla platba předem vyžadována.</li>
        </ul>
        <p>
          Online platby zajišťuje platební brána{' '}
          <a href="https://www.comgate.eu/cs/platebni-brana" target="_blank" rel="noopener" className="font-semibold text-ink-900 hover:text-brand-600 underline">Comgate</a>.
          Poskytovatelem služby je společnost <b>Comgate a.s.</b>, IČO 27924505, Gočárova třída 1754/48b,
          500 02 Hradec Králové, licencovaná platební instituce pod dohledem České národní banky. Platby jsou
          plně zabezpečené a všechny údaje jsou šifrované. Údaje o platební kartě zadáváte výhradně na stránce
          platební brány; provozovatel areálu k nim nemá přístup.
        </p>
        <p>
          <b>Reklamace a dotazy k platbám</b> vyřizuje Comgate a.s.:{' '}
          <a href="mailto:podpora@comgate.cz" className="font-semibold text-ink-900 hover:text-brand-600">podpora@comgate.cz</a>,
          tel.{' '}
          <a href="tel:+420228224267" className="font-semibold text-ink-900 hover:text-brand-600">+420 228 224 267</a>{' '}
          (Gočárova třída 1754/48b, Hradec Králové).
        </p>
      </Block>

      <Block title="3. Kdy se platí">
        <ul className="list-disc list-inside space-y-1.5">
          <li><b>Sportoviště</b> — 100 % ceny předem online; po odeslání rezervace máte 15 minut na dokončení platby, poté se termín uvolní.</li>
          <li><b>Ubytování</b> — provozovatel může požadovat zálohu 30 % až 100 % ceny; rezervace je závazná po jejím uhrazení. Zbývající část hradíte nejpozději při ukončení pobytu.</li>
          <li><b>Dárkové poukazy</b> — 100 % ceny předem online.</li>
        </ul>
      </Block>

      <Block title="4. Dodání služby a potvrzení">
        <p>
          Předmětem koupě je služba poskytnutá v areálu Sobáčov v rezervovaném termínu — nic
          se nezasílá poštou. Ihned po odeslání rezervace obdržíte <b>potvrzení SMS</b> s číslem
          rezervace, variabilním symbolem a odkazem na případný přesun termínu; dva dny před
          příjezdem přijde odkaz na online check-in.
        </p>
        <p>
          <b>Dárkový poukaz</b> dostanete elektronicky (kód v SMS a PDF e-mailem) ihned po
          připsání platby; platí 1 rok od zaplacení.
        </p>
        <p>
          Časy příjezdu a odjezdu podle typu ubytování najdete v sekci{' '}
          <a href="#info-prijezd" className="font-semibold text-ink-900 hover:text-brand-600">Příjezd &amp; odjezd</a>.
        </p>
      </Block>

      <Block title="5. Doklad o platbě">
        <p>
          Daňový doklad vystavuje provozovatel; obdržíte jej elektronicky nebo při vyúčtování
          pobytu na recepci. Platba kartou se na výpisu z účtu zobrazí pod označením platební
          brány Comgate.
        </p>
      </Block>

      <Block title="6. Související dokumenty">
        <p>
          <a href="#podminky" className="font-semibold text-ink-900 hover:text-brand-600">Obchodní podmínky</a> ·{' '}
          <a href="#info-reklamace" className="font-semibold text-ink-900 hover:text-brand-600">Reklamační řád</a> ·{' '}
          <a href="#gdpr" className="font-semibold text-ink-900 hover:text-brand-600">Ochrana osobních údajů</a>
        </p>
      </Block>
    </Shell>
  )
}

export function ReklamacniRad() {
  return (
    <Shell
      title="Reklamační řád"
      lead="Vrácení peněz, storno služby a reklamace — přehledně na jednom místě. Plné znění je v Obchodních podmínkách."
    >
      <Block title="1. Storno rezervace ubytování">
        <ul className="list-disc list-inside space-y-1.5">
          <li>Zrušení nebo změna nejpozději <b>14 dní před příjezdem</b> — <b>zdarma</b>, uhrazená záloha se vrací v plné výši.</li>
          <li>Méně než 14 dní před příjezdem nebo nedojezd — storno poplatek <b>100 %</b> ceny pobytu.</li>
          <li>Nevratné rezervace (Non-Refundable, First Minute) nelze zrušit ani změnit; pobytové balíčky se slevou lze zrušit nejpozději 7 dní předem, jinak 50 %.</li>
          <li>Skupiny a akce se řídí samostatným rozpisem v čl. 6 Obchodních podmínek.</li>
        </ul>
        <p>Rezervaci lze <b>jednou přesunout</b> z odkazu v potvrzovací SMS nejpozději 14 dní před příjezdem.</p>
      </Block>

      <Block title="2. Storno sportovišť">
        <p>
          Zaplacenou rezervaci kurtu nebo hřiště lze <b>jednou přesunout</b> nejpozději
          <b> 2 hodiny před začátkem</b> z odkazu nebo kódu v potvrzovací SMS. Zrušíme-li
          rezervaci my kvůli počasí, dostanete voucher na 100 % hodnoty s platností 90 dní.
          Nevyužitý zaplacený termín bez přesunu propadá.
        </p>
      </Block>

      <Block title="3. Odstoupení od smlouvy a vrácení peněz">
        <p>
          Podle § 1837 písm. j) občanského zákoníku nelze odstoupit od smlouvy o ubytování,
          využití volného času nebo stravování poskytovaných v určeném termínu; pro tyto
          služby platí storno podmínky výše. U <b>dárkového poukazu</b> lze odstoupit do 14 dnů
          od zaplacení, dokud nebyl uplatněn — kód zneplatníme a částku vrátíme.
        </p>
        <p>
          Vrácení peněz provádíme do <b>14 dnů</b> stejným způsobem, jakým byla platba přijata
          (platba kartou → zpět na kartu přes platební bránu, převod → na účet odesílatele).
        </p>
      </Block>

      <Block title="4. Reklamace služby">
        <p>
          Nesplňuje-li poskytnutá služba sjednaný rozsah nebo kvalitu, reklamujte ji
          <b> neprodleně na místě</b> (recepce, +420 775 140 705), aby bylo možné vadu odstranit,
          nebo písemně na{' '}
          <a href="mailto:rezervace.sobacov@gmail.com" className="font-semibold text-ink-900 hover:text-brand-600">rezervace.sobacov@gmail.com</a>{' '}
          s číslem rezervace a popisem. Reklamaci vyřídíme bez zbytečného odkladu, nejpozději
          do <b>30 dnů</b> od uplatnění. Oprávněná reklamace = odstranění vady, náhradní
          plnění, přiměřená sleva nebo vrácení peněz.
        </p>
      </Block>

      <Block title="5. Mimosoudní řešení sporů">
        <p>
          Nedojde-li k dohodě, může se spotřebitel obrátit na Českou obchodní inspekci
          (<a href="https://www.coi.cz" target="_blank" rel="noopener" className="font-semibold text-ink-900 hover:text-brand-600">www.coi.cz</a>)
          jako subjekt mimosoudního řešení spotřebitelských sporů, případně na platformu EU pro
          řešení sporů online (ec.europa.eu/odr).
        </p>
      </Block>

      <Block title="6. Související dokumenty">
        <p>
          <a href="#podminky" className="font-semibold text-ink-900 hover:text-brand-600">Obchodní podmínky</a> ·{' '}
          <a href="#info-platba" className="font-semibold text-ink-900 hover:text-brand-600">Platební a dodací podmínky</a> ·{' '}
          <a href="#gdpr" className="font-semibold text-ink-900 hover:text-brand-600">Ochrana osobních údajů</a>
        </p>
      </Block>
    </Shell>
  )
}
